import { exportCertificates } from "@prostocheck/kep";
import {
  PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY,
  PERSISTED_KEY_SESSION_ENCRYPTION_KEY_STORAGE_KEY,
  PERSISTED_KEY_SESSION_STORAGE_KEY,
} from "@prostocheck/shared";

import {
  IPersistedKeySession,
  IPersistedSessionMeta,
  IPersistedSessionSecret,
  ISavePersistedSessionParams,
} from "./persisted-session.models";
import {
  bytesToBase64,
  decryptSecret,
  encryptSecret,
  exportSessionKey,
  generateSessionKey,
  IEncryptedPayload,
  importSessionKey,
} from "./persisted-session.utils";

/**
 * Persists the decrypted key, its certificates and the password needed to
 * decrypt it again, so a reload can sign the user back in with no re-prompt.
 * The credential is AES-GCM encrypted at rest under a key generated for this
 * record alone; that key is also written to `localStorage`, next to the
 * ciphertext, because a static site has nowhere else to keep it. See
 * ai/docs/kep-signing.md for what this does and does not protect against.
 */
export const savePersistedSession = async ({
  keyBox,
  file,
  password,
  expiresAt,
}: ISavePersistedSessionParams): Promise<void> => {
  const secret: IPersistedSessionSecret = {
    fileBase64: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
    password,
    certificatesBase64: exportCertificates(keyBox).map(bytesToBase64),
  };

  const key = await generateSessionKey();
  const [encrypted, exportedKey] = await Promise.all([
    encryptSecret(key, secret),
    exportSessionKey(key),
  ]);

  const meta: IPersistedSessionMeta = {
    expiresAt,
    savedAt: new Date().toISOString(),
  };

  localStorage.setItem(
    PERSISTED_KEY_SESSION_STORAGE_KEY,
    JSON.stringify(meta),
  );
  localStorage.setItem(
    PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY,
    JSON.stringify(encrypted),
  );
  localStorage.setItem(PERSISTED_KEY_SESSION_ENCRYPTION_KEY_STORAGE_KEY, exportedKey);
};

/**
 * Non-sensitive, so it needs no decryption — read this to decide whether a
 * restore is worth attempting at all before paying for the async round trip
 * through `crypto.subtle`.
 */
export const readPersistedSessionMeta = (): IPersistedSessionMeta | null => {
  const raw = localStorage.getItem(PERSISTED_KEY_SESSION_STORAGE_KEY);

  if (!raw) return null;

  try {
    return JSON.parse(raw) as IPersistedSessionMeta;
  } catch {
    return null;
  }
};

/**
 * Decrypts the persisted key, its certificates and its password — the one
 * async step in reading a session back, hence separate from
 * `readPersistedSessionMeta`. `null` for anything short of a clean decrypt:
 * a missing piece, corrupted JSON, a key that no longer matches.
 */
export const loadPersistedSession = async (): Promise<IPersistedKeySession | null> => {
  const meta = readPersistedSessionMeta();
  const encryptedRaw = localStorage.getItem(
    PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY,
  );
  const keyBase64 = localStorage.getItem(
    PERSISTED_KEY_SESSION_ENCRYPTION_KEY_STORAGE_KEY,
  );

  if (!meta || !encryptedRaw || !keyBase64) return null;

  try {
    const key = await importSessionKey(keyBase64);
    const payload = JSON.parse(encryptedRaw) as IEncryptedPayload;
    const secret = await decryptSecret(key, payload);

    return { ...meta, ...secret };
  } catch {
    return null;
  }
};

export const clearPersistedSession = (): void => {
  localStorage.removeItem(PERSISTED_KEY_SESSION_STORAGE_KEY);
  localStorage.removeItem(PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY);
  localStorage.removeItem(PERSISTED_KEY_SESSION_ENCRYPTION_KEY_STORAGE_KEY);
};

export const isPersistedSessionExpired = (
  meta: IPersistedSessionMeta,
): boolean => new Date(meta.expiresAt).getTime() <= Date.now();
